import React, { Component } from 'react';
import Carousel from 'react-bootstrap/Carousel';
import A11yFontIcon from 'components/A11yFontIcon';
import { PinIcon } from 'components/Icon';
import PaginationControls from 'components/PaginationControls';
import TextButton from 'components/TextButton';
import {
  clearHiddenPinnedPosts,
  getHiddenPinnedPosts,
  setHiddenPinnedPosts
} from 'utils/localStorage';
import './FeedPinnedPosts.css';

function loadShouldHide(pinnedPosts) {
  const pinnedIdSet = new Set(pinnedPosts.map(post => post.id));
  const hiddenIdSet = new Set(getHiddenPinnedPosts());
  for (let id of pinnedIdSet) {
    if (!hiddenIdSet.has(id)) {
      return false;
    }
  }
  return true;
}

class FeedPinnedPosts extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeIndex: 0,
      buttonsEnabled: true,
      pinnedHidden: loadShouldHide(props.pinnedPosts)
    };

    this.containerEl = React.createRef();
  }

  setPinnedHidden = hidden => {
    if (hidden) {
      setHiddenPinnedPosts(this.props.pinnedPosts.map(post => post.id));
    } else {
      clearHiddenPinnedPosts();
    }
    this.setState({ pinnedHidden: hidden });
  };

  togglePinnedHidden = () => {
    this.setPinnedHidden(!this.state.pinnedHidden);
  };

  setPage = newPage => {
    if (newPage !== this.state.activeIndex) {
      const newState = { activeIndex: newPage };
      if (!this.state.pinnedHidden) {
        newState.buttonsEnabled = false;
        this.updateCarouselHeight(this.state.activeIndex, newPage);
      }
      this.setState(newState);
    }
    if (this.state.pinnedHidden) {
      this.togglePinnedHidden();
    }
  };

  setButtonsEnabled = enabled => {
    this.setState({ buttonsEnabled: enabled });
  };

  updateCarouselHeight = (oldPage, newPage) => {
    const containerEl = this.containerEl.current;
    const carouselEl =
      containerEl && containerEl.querySelector('.feed-carousel-post');
    const carouselInnerEl = carouselEl && carouselEl.firstChild;
    if (
      carouselInnerEl &&
      carouselInnerEl.childElementCount > oldPage &&
      carouselInnerEl.childElementCount > newPage
    ) {
      const oldPageHeight =
        carouselInnerEl.children[oldPage].clientHeight + 'px';
      carouselEl.style.height = oldPageHeight;
      setTimeout(() => {
        const newPageHeight =
          carouselInnerEl.children[newPage].clientHeight + 'px';
        carouselEl.style.height = newPageHeight;
      }, 0);
    }
  };

  clearCarouselHeight = () => {
    const containerEl = this.containerEl.current;
    const carouselEl =
      containerEl && containerEl.querySelector('.feed-carousel-post');
    if (carouselEl) {
      carouselEl.style.removeProperty('height');
    }
  };

  handleSlideEnd = () => {
    this.setButtonsEnabled(true);
    this.clearCarouselHeight();
  };

  componentDidUpdate(prevProps) {
    if (this.props.pinnedPosts !== prevProps.pinnedPosts) {
      const shouldHide = loadShouldHide(this.props.pinnedPosts);
      this.setPinnedHidden(shouldHide);
      if (!shouldHide) {
        const firstPostId =
          this.props.pinnedPosts[0] && this.props.pinnedPosts[0].id;
        const prevFirstPostId =
          prevProps.pinnedPosts[0] && prevProps.pinnedPosts[0].id;
        if (firstPostId !== prevFirstPostId) {
          this.setPage(0);
        }
      }
    }
  }

  render() {
    const { pinnedPosts, renderPostFn, showLegacyFeed } = this.props;
    const { activeIndex, buttonsEnabled, pinnedHidden } = this.state;
    const clampedIndex = Math.min(activeIndex, pinnedPosts.length - 1);
    const legacyClassName = showLegacyFeed ? 'legacy-background' : '';
    const feedPinnedPostsClassName = `feed-pinned-posts ${legacyClassName}`;

    return (
      <div className={feedPinnedPostsClassName} ref={this.containerEl}>
        {pinnedHidden && (
          <span className="pinned-posts-title">Community Messages</span>
        )}
        <TextButton
          className="pinned-minimize-button"
          onClick={this.togglePinnedHidden}
        >
          {pinnedHidden ? (
            <A11yFontIcon
              className="maximize-icon"
              icon="plus"
              fixed
              size="2x"
              alt="Show Pinned Posts"
            />
          ) : (
            <A11yFontIcon
              className="minimize-icon"
              icon="minus"
              fixed
              size="2x"
              alt="Hide Pinned Posts"
            />
          )}
          <PinIcon
            className="user-pinned-icon base"
            filled={true}
            alt="Pinned to top"
          />
        </TextButton>
        {!pinnedHidden && (
          <>
            {pinnedPosts.length > 1 && (
              <PaginationControls
                controls={pinnedPosts}
                activeIndex={clampedIndex}
                onPaginationChange={this.setPage}
                withSound={false}
                buttonsEnabled={buttonsEnabled}
              />
            )}
            <Carousel
              className="feed-carousel-post"
              controls={false}
              interval={null}
              indicators={false}
              activeIndex={clampedIndex}
              onSlid={this.handleSlideEnd}
              key={pinnedPosts.length} // Workaround for Carousel stuck index bug
            >
              {pinnedPosts.map((post, index) =>
                renderPostFn(post, (postElement, wrapperClasses) => (
                  <Carousel.Item key={`key-${post.id}`}>
                    {postElement}
                  </Carousel.Item>
                ))
              )}
            </Carousel>
          </>
        )}
      </div>
    );
  }
}

export default FeedPinnedPosts;
